'use client';

import { useMemo, useState } from 'react';
import {
  diffDates,
  formatDateIso,
  formatDateLong,
  parseDateInput,
  shiftDate,
} from '@/lib/browser-calc-tools-extra';

type Mode = 'difference' | 'add';

export default function BrowserDaysBetweenDates() {
  const [mode, setMode] = useState<Mode>('difference');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [inclusive, setInclusive] = useState(false);
  const [baseDate, setBaseDate] = useState('');
  const [amount, setAmount] = useState('30');
  const [direction, setDirection] = useState<'add' | 'subtract'>('add');

  const difference = useMemo(() => {
    const start = parseDateInput(startDate);
    const end = parseDateInput(endDate);
    if (!start || !end) {
      return null;
    }
    return { start, end, breakdown: diffDates(start, end, inclusive) };
  }, [startDate, endDate, inclusive]);

  const addResult = useMemo(() => {
    const base = parseDateInput(baseDate);
    const days = Number(amount);
    if (!base || !Number.isFinite(days)) {
      return null;
    }
    const delta = direction === 'add' ? Math.round(days) : -Math.round(days);
    const result = shiftDate(base, delta);
    return { base, result, delta };
  }, [baseDate, amount, direction]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Days Between Dates</span>
        <h2 className="tool-live-title">Count the days between two dates</h2>
        <p className="tool-live-subtitle">
          Pick two dates to see the gap in days, weeks, and a years / months / days breakdown, or
          switch to add or subtract days from a date. Everything runs in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => setMode('difference')}
              type="button"
            >
              Difference
            </button>
            <button
              className="button button-secondary"
              onClick={() => setMode('add')}
              type="button"
            >
              Add / subtract
            </button>
          </div>

          {mode === 'difference' ? (
            <div className="tool-live-controls">
              <label className="tool-live-field tool-live-field-full">
                <span>Start date</span>
                <input
                  onChange={(event) => setStartDate(event.target.value)}
                  type="date"
                  value={startDate}
                />
              </label>
              <label className="tool-live-field tool-live-field-full">
                <span>End date</span>
                <input
                  onChange={(event) => setEndDate(event.target.value)}
                  type="date"
                  value={endDate}
                />
              </label>
              <label
                className="tool-live-field tool-live-field-full"
                style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}
              >
                <input
                  checked={inclusive}
                  onChange={(event) => setInclusive(event.target.checked)}
                  style={{ width: 'auto' }}
                  type="checkbox"
                />
                <span>Include the end date (count both endpoints)</span>
              </label>
            </div>
          ) : (
            <div className="tool-live-controls">
              <label className="tool-live-field tool-live-field-full">
                <span>Start date</span>
                <input
                  onChange={(event) => setBaseDate(event.target.value)}
                  type="date"
                  value={baseDate}
                />
              </label>
              <label className="tool-live-field">
                <span>Days</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => setAmount(event.target.value)}
                  type="text"
                  value={amount}
                />
              </label>
              <label className="tool-live-field">
                <span>Direction</span>
                <select
                  onChange={(event) => setDirection(event.target.value as 'add' | 'subtract')}
                  value={direction}
                >
                  <option value="add">Add days</option>
                  <option value="subtract">Subtract days</option>
                </select>
              </label>
            </div>
          )}
        </section>

        <section className="tool-live-preview">
          {mode === 'difference' ? (
            difference ? (
              <>
                <div className="tool-html-meta-grid">
                  <div className="tool-live-field">
                    <span>Total days</span>
                    <input readOnly type="text" value={String(difference.breakdown.totalDays)} />
                  </div>
                  <div className="tool-live-field">
                    <span>Years / months / days</span>
                    <input
                      readOnly
                      type="text"
                      value={`${difference.breakdown.years}y ${difference.breakdown.months}m ${difference.breakdown.days}d`}
                    />
                  </div>
                  <div className="tool-live-field">
                    <span>Weeks and days</span>
                    <input
                      readOnly
                      type="text"
                      value={`${difference.breakdown.weeks}w ${difference.breakdown.remainderDays}d`}
                    />
                  </div>
                  <div className="tool-live-field">
                    <span>Total weeks</span>
                    <input readOnly type="text" value={String(difference.breakdown.totalWeeks)} />
                  </div>
                  <div className="tool-live-field">
                    <span>Total hours</span>
                    <input
                      readOnly
                      type="text"
                      value={difference.breakdown.totalHours.toLocaleString('en-US')}
                    />
                  </div>
                  <div className="tool-live-field">
                    <span>Total minutes</span>
                    <input
                      readOnly
                      type="text"
                      value={difference.breakdown.totalMinutes.toLocaleString('en-US')}
                    />
                  </div>
                </div>
                <p className="tool-live-panel-note">
                  From {formatDateLong(difference.start)} to {formatDateLong(difference.end)}.
                </p>
              </>
            ) : (
              <p className="tool-live-panel-note">
                Pick a valid start and end date to see the gap.
              </p>
            )
          ) : addResult ? (
            <>
              <div className="tool-html-meta-grid">
                <div className="tool-live-field">
                  <span>Result date</span>
                  <input readOnly type="text" value={formatDateIso(addResult.result)} />
                </div>
                <div className="tool-live-field">
                  <span>Day of week</span>
                  <input readOnly type="text" value={formatDateLong(addResult.result)} />
                </div>
              </div>
              <p className="tool-live-panel-note">
                {direction === 'add' ? 'Added' : 'Subtracted'} {Math.abs(addResult.delta)} day
                {Math.abs(addResult.delta) === 1 ? '' : 's'} to {formatDateLong(addResult.base)}.
              </p>
            </>
          ) : (
            <p className="tool-live-panel-note">Pick a start date and a number of days.</p>
          )}
        </section>
      </div>
    </div>
  );
}
